import {
  Add as AddIcon,
  Edit as EditIcon,
  Groups as GroupsIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  SwapHoriz as SwapHorizIcon,
  Sync as SyncIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import { formatMoney } from "../../../partner-commission/utils/formatMoney";
import MarketGrowthDataTable, {
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import PartnerCommissionGroupDialog from "../components/PartnerCommissionGroupDialog";
import PartnerDetailsDialog from "../components/PartnerDetailsDialog";
import PartnerFormDialog from "../components/PartnerFormDialog";
import PartnerReferralLinkDialog from "../components/PartnerReferralLinkDialog";
import PartnerStatusDialog from "../components/PartnerStatusDialog";
import PartnerTeamDialog from "../components/PartnerTeamDialog";
import { getEnumLabel } from "../constants/commissionEnums";
import {
  PARTNER_STATUS_LABELS,
  PARTNER_TYPE_LABELS,
  isPartnerActive,
} from "../constants/partnerEnums";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const formatPersonCell = (name, mobile) => (
  <>
    <Typography variant="body2" fontWeight={600}>
      {name || "—"}
    </Typography>
    {mobile ? (
      <Typography variant="caption" color="text.secondary" display="block">
        {mobile}
      </Typography>
    ) : null}
  </>
);

const MarketGrowthPartnersPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [statusDialog, setStatusDialog] = useState({ open: false, partner: null });
  const [groupDialog, setGroupDialog] = useState({ open: false, partner: null });
  const [referralDialog, setReferralDialog] = useState({ open: false, partner: null });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, partner: null });
  const [teamDialog, setTeamDialog] = useState({ open: false, partner: null });

  const loadGroups = useCallback(async () => {
    const res = await MarketGrowthAdminApi.getGroups();
    const normalized = MarketGrowthAdminApi.normalizeList(
      res,
      "بارگذاری گروه‌ها ناموفق بود."
    );
    if (!normalized.error) {
      setGroups(Array.isArray(normalized.data) ? normalized.data : []);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getLevel1Partners();
      const { error: err, data } = MarketGrowthAdminApi.normalizeList(
        res,
        "بارگذاری مدیران فروش ناموفق بود."
      );
      if (err) {
        setError(err);
        setItems([]);
        return;
      }
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    load();
  }, [load]);

  const groupMap = useMemo(
    () =>
      groups.reduce((acc, g) => {
        acc[g.id] = g.title;
        return acc;
      }, {}),
    [groups]
  );

  const openCreate = () => {
    setEditingItem(null);
    setFormOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const res = await MarketGrowthAdminApi.getPartnerById(row.id);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری جزئیات نماینده ناموفق بود."
      );
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      // normalize shape and convert enum numbers to frontend string names
      const shaped = MarketGrowthAdminApi.normalizePartnerProfile(normalized.data);
      const forForm = MarketGrowthAdminApi.mapPartnerEnumsToNames(shaped);
      setEditingItem(forForm);
      setFormOpen(true);
    } catch (e) {
      toast.error(e?.message || "خطا در بارگذاری");
    }
  };

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      const res = editingItem
        ? await MarketGrowthAdminApi.updatePartner(editingItem.id, payload)
        : await MarketGrowthAdminApi.createPartner(payload);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        editingItem ? "ویرایش نماینده ناموفق بود." : "ایجاد نماینده ناموفق بود."
      );
      if (normalized.error) return { error: normalized.error };
      if (!editingItem && normalized.data?.userWasCreated) {
        toast.success(
          `نماینده ایجاد شد. اکانت جدید ساخته شد — موبایل: ${normalized.data.mobileNumber}، رمز: ${normalized.data.initialPasswordHint || "123456"}`
        );
      } else {
        toast.success(editingItem ? "نماینده ویرایش شد." : "نماینده ایجاد شد.");
      }
      setFormOpen(false);
      setEditingItem(null);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  const handleStatusSubmit = async (status) => {
    if (!statusDialog.partner) return {};
    setSaving(true);
    try {
      const res = await MarketGrowthAdminApi.patchPartnerStatus(
        statusDialog.partner.id,
        status
      );
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "تغییر وضعیت ناموفق بود."
      );
      if (normalized.error) return { error: normalized.error };
      toast.success("وضعیت نماینده به‌روزرسانی شد.");
      setStatusDialog({ open: false, partner: null });
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  const handleGroupSubmit = async (commissionGroupId) => {
    if (!groupDialog.partner) return {};
    setSaving(true);
    try {
      const res = await MarketGrowthAdminApi.patchPartnerCommissionGroup(
        groupDialog.partner.id,
        commissionGroupId
      );
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "تغییر گروه پورسانتی ناموفق بود."
      );
      if (normalized.error) return { error: normalized.error };
      toast.success("گروه پورسانتی به‌روزرسانی شد.");
      setGroupDialog({ open: false, partner: null });
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  const tableRows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        managerName: row.displayName || row.mobileNumber || String(row.userId),
        managerSearch: [row.displayName, row.mobileNumber, row.referralCode]
          .filter(Boolean)
          .join(" "),
        partnerTypeLabel: getEnumLabel(PARTNER_TYPE_LABELS, row.partnerType),
        statusLabel: getEnumLabel(PARTNER_STATUS_LABELS, row.status),
        groupTitle: groupMap[row.commissionGroupId] || String(row.commissionGroupId),
        teamMembersLabel: `${row.teamMemberCount ?? 0} نفر (${row.activeTeamMemberCount ?? 0} فعال)`,
        teamCommissionLabel: `${formatMoney(row.teamTotalCommissionGenerated ?? 0)} / پرداخت: ${formatMoney(row.teamTotalPaidCommission ?? 0)}`,
        joinedAtLabel: formatDate(row.joinedAt),
        joinedAt: row.joinedAt,
      })),
    [groupMap, items]
  );

  const headCells = useMemo(
    () => [
      {
        id: "managerSearch",
        label: "مدیر فروش",
        render: (_, row) => (
          <>
            {formatPersonCell(row.managerName, row.mobileNumber)}
            {!isPartnerActive(row.status) ? (
              <Chip size="small" label="غیرفعال" color="warning" sx={{ mt: 0.5 }} />
            ) : null}
          </>
        ),
      },
      { id: "partnerTypeLabel", label: "نوع" },
      { id: "statusLabel", label: "وضعیت", type: "chip" },
      { id: "groupTitle", label: "گروه پورسانتی" },
      { id: "referralCode", label: "کد معرف" },
      { id: "teamMembersLabel", label: "اعضای تیم" },
      { id: "teamCommissionLabel", label: "پورسانت تیم" },
      { id: "joinedAt", label: "تاریخ ایجاد", type: "date" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <GroupsIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setTeamDialog({ open: true, partner: row }),
        color: "primary",
        tooltip: "مشاهده تیم",
      },
      {
        icon: <InfoIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setDetailsDialog({ open: true, partner: row }),
        color: "primary",
        tooltip: "جزئیات",
      },
      {
        icon: <EditIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: openEdit,
        color: "primary",
        tooltip: "ویرایش",
      },
      {
        icon: <SwapHorizIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setStatusDialog({ open: true, partner: row }),
        color: "secondary",
        tooltip: "تغییر وضعیت",
      },
      {
        icon: <TuneIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setGroupDialog({ open: true, partner: row }),
        color: "secondary",
        tooltip: "تغییر گروه",
      },
      {
        icon: <LinkIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setReferralDialog({ open: true, partner: row }),
        color: "info",
        tooltip: "لینک معرف",
      },
    ],
    []
  );

  return (
    <MarketGrowthPageShell
      title="نمایندگان"
      description="مدیریت مدیران فروش (سطح یک) و آمار تیم هر نماینده."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          ایجاد نماینده جدید
        </Button>
        <Tooltip title="بارگذاری مجدد">
          <IconButton onClick={load}>
            <SyncIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            defaultSort={{ orderBy: "joinedAt", order: "desc" }}
            tableMinWidth={1200}
            actionsMode="menu"
          />
        </CardContent>
      </Card>

      <PartnerFormDialog
        open={formOpen}
        onClose={() => !saving && (setFormOpen(false), setEditingItem(null))}
        onSubmit={handleSubmit}
        initialData={editingItem}
        groups={groups}
        level1Partners={items}
        saving={saving}
      />

      <PartnerStatusDialog
        open={statusDialog.open}
        onClose={() => !saving && setStatusDialog({ open: false, partner: null })}
        partner={statusDialog.partner}
        onSubmit={handleStatusSubmit}
        saving={saving}
      />

      <PartnerCommissionGroupDialog
        open={groupDialog.open}
        onClose={() => !saving && setGroupDialog({ open: false, partner: null })}
        partner={groupDialog.partner}
        groups={groups}
        onSubmit={handleGroupSubmit}
        saving={saving}
      />

      <PartnerReferralLinkDialog
        open={referralDialog.open}
        onClose={() => setReferralDialog({ open: false, partner: null })}
        partner={referralDialog.partner}
      />

      <PartnerDetailsDialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, partner: null })}
        partner={detailsDialog.partner}
        groupMap={groupMap}
        parentMap={{}}
      />

      <PartnerTeamDialog
        open={teamDialog.open}
        onClose={() => setTeamDialog({ open: false, partner: null })}
        partner={teamDialog.partner}
      />
    </MarketGrowthPageShell>
  );
};

export default MarketGrowthPartnersPage;
